import TooltipCommon from 'components/common/TooltipCommon';
import {
  CALCULATOR_DESKTOP_WIDTH,
  CALCULATOR_FORM_PAYOUT_METHOD_NAME,
  CALCULATOR_FORM_RECEIVER_GET_NAME,
  CALCULATOR_FORM_SEND_TO_NAME,
  SENDING_AMOUNT_DISCOUNT_PERCENTS,
} from 'constants';
import CalculatorRoundingAnimationLottie from 'features/calculatorRounding/components/CalculatorRoundingAnimationLottie';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { format } from 'helpers';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { numberUtils } from 'utils';

const TotalPayForm = ({ information, countryConfig, currenciesSupport }) => {
  const { t } = useLang();

  const [originalFee, setOriginalFee] = useState(null);
  const { calculatorRoundingPublicResponse, isCalculatorRounding } =
    useCalculatorRoundingStore();
  const { originalFee: originalFeeStore } =
    calculatorRoundingPublicResponse || {};

  const { currentCurrencySelected } = information || {};

  const { code: dmCode } =
    information[CALCULATOR_FORM_PAYOUT_METHOD_NAME] || {};

  const onlyCurrencySelectedByVND =
    currentCurrencySelected?.currencyCode === 'VND';

  const parseAmountValue = (value) => {
    return value && format.toAmountFloatStr(numberUtils.mathRound(value));
  };

  const sendAmount = numberUtils.mathRound(
    information[CALCULATOR_FORM_SEND_TO_NAME] || 0,
  );
  const getAmount = numberUtils.mathRound(
    information[CALCULATOR_FORM_RECEIVER_GET_NAME] || 0,
  );
  const sendTotalAmount = numberUtils.mathRound(sendAmount);
  const isCalculatorRoundingBySendAmount =
    isCalculatorRounding && sendTotalAmount;

  const feeOriginAmount = numberUtils.mathRound(originalFee?.feeAmount || 0);

  const feeAmount = numberUtils.mathRound(
    information['feeAmount']?.feeAmount || 0,
  );

  const totalPayAmount = numberUtils.mathRound(feeAmount + sendTotalAmount);

  const discountPercent = () => {
    return SENDING_AMOUNT_DISCOUNT_PERCENTS.find((o) => {
      if (o.range.length === 1 && sendTotalAmount >= o.range[0]) return o;

      if (sendTotalAmount >= o.range[0] && sendTotalAmount <= o.range[1])
        return o;

      return null;
    });
  };

  const isShowDiscountPercent = Boolean(
    onlyCurrencySelectedByVND &&
    feeAmount &&
    discountPercent() &&
    feeOriginAmount !== feeAmount,
  );

  const discountedAmount = (feeOriginAmount, feeAmount) => {
    return feeOriginAmount - feeAmount;
  };

  useEffect(() => {
    const isCurrentCurrencySelectedSupported = Boolean(
      currenciesSupport?.find(
        (currency) =>
          currency?.currencyCode === currentCurrencySelected?.currencyCode,
      ),
    );

    if (
      !isCurrentCurrencySelectedSupported ||
      !sendTotalAmount ||
      !dmCode ||
      isEmpty(currentCurrencySelected)
    ) {
      setOriginalFee(null);
      return;
    }
    if (countryConfig && sendTotalAmount && dmCode) {
      setOriginalFee(originalFeeStore);
    }
  }, [countryConfig, sendTotalAmount, getAmount, dmCode, originalFeeStore]);

  const renderSendingAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>{`${parseAmountValue(
        sendTotalAmount,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderFeeAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue className="fee-value">
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue className="fee-value">
        {`${parseAmountValue(feeOriginAmount)} AUD`}
      </CalculatorFieldValue>
    );
  };

  const renderDiscountAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue className="fee-value">
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>
        {`-${parseAmountValue(
          discountedAmount(feeOriginAmount, feeAmount),
        )} AUD`}
      </CalculatorFieldValue>
    );
  };
  const renderDiscountSection = () => {
    if (
      sendTotalAmount <= 0 ||
      discountedAmount(feeOriginAmount, feeAmount) <= 0
    )
      return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel className="fee-value">
          {t('label_fee_discount')}
          {isShowDiscountPercent && !isCalculatorRounding && (
            <CalculatorFieldValueChild className="summary-discount">
              {discountPercent() && (
                <TooltipCommon
                  text={t(discountPercent().tooltip)}
                  icon={discountPercent().imgSrc}
                  isPopover
                  isDangerouslySetInnerHTML={true}
                />
              )}
            </CalculatorFieldValueChild>
          )}
        </CalculatorFieldLabel>
        {renderDiscountAmountValue()}
      </CalculatorFieldWrap>
    );
  };

  const renderTotalToPayValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorTotalValue>
          <CalculatorRoundingAnimationLottie width="25px" height="25px" />
        </CalculatorTotalValue>
      );
    return (
      <CalculatorTotalValue>{`${parseAmountValue(
        totalPayAmount,
      )} AUD`}</CalculatorTotalValue>
    );
  };

  return (
    <CalculatorFormStyled>
      <CalculatorFieldWrap>
        <CalculatorFieldLabel>{t('form_you_send_2')}</CalculatorFieldLabel>
        {renderSendingAmountValue()}
      </CalculatorFieldWrap>
      <CalculatorFieldWrap>
        <CalculatorFieldLabel>{t('label_fee')}</CalculatorFieldLabel>
        {renderFeeAmountValue()}
      </CalculatorFieldWrap>
      {renderDiscountSection()}
      {/* <Learnmore href="/">Learn more about fee</Learnmore> */}
      <Divider />
      <CalculatorTotal>
        <CalculatorTotalLabel>{t('label_total_to_pay')}</CalculatorTotalLabel>
        {renderTotalToPayValue()}
      </CalculatorTotal>
    </CalculatorFormStyled>
  );
};

const CalculatorFormStyled = styled.div`
  width: 100%;
  background: var(--ds-bg-2);
  border-radius: 10px;
  padding: 16px;

  @media screen and (max-width: ${CALCULATOR_DESKTOP_WIDTH +
    1}px) and (min-width: ${CALCULATOR_DESKTOP_WIDTH - 1}px) {
    padding: 13px 16px;
  }
`;
const CalculatorFieldWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  margin-bottom: 0px;
`;
const CalculatorFieldLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);
  display: flex;
  align-items: center;

  margin: 0;
  margin-bottom: 8px;
  padding: 0;

  & span {
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
  }
`;
const CalculatorFieldValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: var(--ds-c-grey-dark);
  text-align: right;

  margin: 0;
  padding: 0;

  &.fee-value {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &.summary-discount {
    color: var(--ds-c-grey-hover);
    display: flex;
    align-items: center;
  }

  & span {
    font-size: 14px;
    line-height: 17px;
    color: #ffffff;

    background-color: var(--ds-c-green-default);
    padding: 2px 8px;
    border-radius: 2px;
    margin-right: 8px;
  }

  & button.btn-primary {
    display: flex;
  }
`;
const CalculatorFieldValueChild = styled.span`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: var(--ds-c-grey-dark);
  text-align: right;

  margin: 0;
  padding: 0;

  &.fee-value {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &.summary-discount {
    color: var(--ds-c-grey-hover);
    display: flex;
    align-items: center;
  }

  & span {
    font-size: 14px;
    line-height: 17px;
    color: #ffffff;

    background-color: var(--ds-c-green-default);
    padding: 2px 8px;
    border-radius: 2px;
    margin-right: 8px;
  }

  & button.btn-primary {
    display: flex;
  }
`;

const CalculatorTotal = styled.div`
  margin-inline: -8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  padding: 0px 8px;
  background: transparent;
  border-radius: 8px;
`;
const CalculatorTotalLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;
  color: var(--ds-c-blue);

  margin: 0;
  padding: 0;
`;
const CalculatorTotalValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 800;
  font-size: 20px;
  line-height: 25px;
  text-transform: uppercase;
  color: var(--ds-c-blue);
  text-align: right;

  margin: 0;
  padding: 0;
`;

// const Learnmore = styled.a`
//   color: var(--ds-c-blue);
//   font-size: 12px;
//   font-weight: 500;
//   line-height: 15px;
//   text-decoration: none;
// `;

const Divider = styled.div`
  height: 1px;
  background-color: #fff;
  margin-top: 10px;
  margin-bottom: 10px;
  line-height: 20px;
`;

export default TotalPayForm;
